import Analytics from '@/utils/analytics';
import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import ThumbsDownActiveIcon from '@assets/icons/thumbs-down-active.svg';
import ThumbsDownIcon from '@assets/icons/thumbs-down.svg';
import ThumbsUpActiveIcon from '@assets/icons/thumbs-up-active.svg';
import ThumbsUpIcon from '@assets/icons/thumbs-up.svg';
import {
  ActionType,
  OrderReviewActions,
  RatingValue,
  ReviewState,
  SubmitFormPayload,
} from './hooks/use-review-form-reducer';
import { Button } from '@/components/button';
import { CircledIcon } from '@/components/circled-icon';
import { createStyles } from '@/contexts/theme-context';
import { DEFAULT_CURRENCY_CODE } from '@/utils/money';
import { Image, Text, TouchableOpacity, View } from 'react-native';
import { Props as ListItemProps } from '@/components/list-item';
import {
  OrderItemList,
  OrderItemListItemType,
} from '@/components/order-item-list';
import {
  OrderItemListFieldsFragment,
  OrderItemRatingFieldsFragment,
} from '@/types';
import { ReviewForm } from './review-form';
import { SCREEN_PADDING, TYPOGRAPHY } from '@/styles';
import { setNextLayoutAnimation } from '@/utils/layout-animation';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';

export type OrderItemDataType = OrderItemListFieldsFragment & {
  order_item_rating?: OrderItemRatingFieldsFragment | null;
};

interface Props {
  dispatch: React.Dispatch<ActionType>;
  displayItemCode: boolean;
  displayItemPrices: boolean;
  isSubmitting: boolean;
  onDonePressed: () => void;
  orderItems: OrderItemDataType[];
  orderUUID: string;
  reviewState: ReviewState;
  useInvoicePrices: boolean;
}

export const ShipmentChecklist: React.FC<Props> = ({
  dispatch,
  displayItemCode,
  displayItemPrices,
  isSubmitting,
  onDonePressed,
  orderItems,
  orderUUID,
  reviewState,
  useInvoicePrices,
}) => {
  const { bottom } = useSafeAreaInsets();
  const { currencyCode } = useVariantPackInfoContext();
  const [styles, theme] = useStyles();

  const listItemProps = useMemo<
    Partial<ListItemProps<OrderItemListItemType>>
  >(() => {
    return {
      listItemStyle: (option) => {
        const itemsInGroup = option.metadata!.orderItems;
        const hasNegativeRating = itemsInGroup.some((oi) => {
          const orderItem = oi.item;
          return (
            orderItem.metadata.orderItemRating?.rating ===
            RatingValue.ThumbsDown
          );
        });
        return {
          backgroundColor: hasNegativeRating
            ? theme.colors.cautionHighlight
            : theme.colors.white,
          paddingHorizontal: SCREEN_PADDING,
        };
      },
      renderListItemFooter: (option) => {
        const { reviews } = reviewState;
        const review = reviews[option.value];
        const noReview = !review || review.rating === RatingValue.ThumbsUp;
        const noContent =
          review && !review.comment && review.photoUrlList.length === 0;
        if (noReview || noContent) {
          return null;
        }

        return (
          <View style={styles.itemFooter}>
            <Text
              numberOfLines={1}
              style={[TYPOGRAPHY.p, styles.ratingPreview]}
            >
              {review.comment}
            </Text>
            <View style={styles.reviewImages}>
              {review.photoUrlList.map((photoUrl, index: number) => (
                <Image
                  alt={`review image ${index + 1} of ${review.photoUrlList.length}`}
                  key={photoUrl}
                  source={{ uri: photoUrl }}
                  style={styles.reviewImage}
                />
              ))}
            </View>
          </View>
        );
      },
      renderRightComponent: (option) => {
        const hasThumbsUp =
          reviewState.reviews[option.value] &&
          reviewState.reviews[option.value].rating === RatingValue.ThumbsUp;
        const hasThumbsDown =
          reviewState.reviews[option.value] &&
          reviewState.reviews[option.value].rating === RatingValue.ThumbsDown;

        return (
          <View style={styles.reviewIcons}>
            <TouchableOpacity
              onPress={() => {
                Analytics.track('order_review.order_item_thumbs_down.press', {
                  orderItemUUID: option.value,
                  orderUUID,
                });

                setNextLayoutAnimation();
                dispatch({
                  payload: { uuid: String(option.value) },
                  type: OrderReviewActions.THUMBS_DOWN,
                });
              }}
              testID="thumbs-down-icon"
            >
              {hasThumbsDown ? (
                <ThumbsDownActiveIcon
                  accentFill={theme.colors.accent}
                  fill={theme.colors.primary}
                  height={48}
                  width={48}
                />
              ) : (
                <CircledIcon
                  Icon={ThumbsDownIcon}
                  iconHeight={48}
                  iconWidth={48}
                />
              )}
            </TouchableOpacity>
            <TouchableOpacity
              onPress={() => {
                Analytics.track('order_review.order_item_thumbs_up.press', {
                  orderItemUUID: option.value,
                  orderUUID,
                });

                reviewState.selectedItemUUID && setNextLayoutAnimation();
                dispatch({
                  payload: { uuid: String(option.value) },
                  type: OrderReviewActions.THUMBS_UP,
                });
              }}
              testID="thumbs-up-icon"
            >
              {hasThumbsUp ? (
                <ThumbsUpActiveIcon
                  accentFill={theme.colors.accent}
                  fill={theme.colors.primary}
                  height={48}
                  width={48}
                />
              ) : (
                <CircledIcon
                  Icon={ThumbsUpIcon}
                  iconHeight={48}
                  iconWidth={48}
                />
              )}
            </TouchableOpacity>
          </View>
        );
      },
    };
  }, [dispatch, orderUUID, reviewState, styles, theme]);

  const visibleItems = useMemo(() => {
    return reviewState.selectedItemUUID
      ? orderItems.filter((item) => item.uuid === reviewState.selectedItemUUID)
      : orderItems;
  }, [orderItems, reviewState.selectedItemUUID]);

  return (
    <OrderItemList
      displayItemCode={displayItemCode}
      displayItemPrices={displayItemPrices}
      displayPlaceholder={false}
      extraData={{ reviewState }}
      fallbackCurrencyCode={currencyCode || DEFAULT_CURRENCY_CODE}
      hideMissingItems
      hidePrices
      ListFooterComponent={() => {
        const { selectedItemUUID, reviews } = reviewState;
        if (selectedItemUUID) {
          return (
            <ReviewForm
              initialReview={reviews[selectedItemUUID]}
              onSubmit={(payload: SubmitFormPayload) => {
                setNextLayoutAnimation();
                dispatch({
                  payload: { uuid: selectedItemUUID, ...payload },
                  type: OrderReviewActions.SUBMIT_FORM,
                });
              }}
              orderUUID={orderUUID}
            />
          );
        }
        const isButtonDisabled =
          Object.keys(reviewState.reviews).length < orderItems.length;

        return (
          <Button
            disabled={isButtonDisabled}
            loading={isSubmitting}
            onPress={onDonePressed}
            style={styles.doneButton}
            trackingData={{
              name: 'shipment_checklist.done_button',
              properties: { orderUUID },
            }}
          >
            {i18n.t('submit')}
          </Button>
        );
      }}
      listItemProps={listItemProps}
      orderItems={visibleItems}
      scrollViewProps={{
        contentContainerStyle: {
          paddingBottom: Math.max(bottom, SCREEN_PADDING),
        },
      }}
      useInvoicePrices={useInvoicePrices}
    />
  );
};

const useStyles = createStyles((theme) => ({
  doneButton: {
    marginHorizontal: SCREEN_PADDING,
  },
  itemFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginLeft: 40,
    marginTop: 12,
  },
  ratingPreview: {
    color: theme.colors.inactiveText,
    flex: 2,
  },
  reviewIcons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: 126,
  },
  reviewImage: {
    height: 32,
    marginLeft: 8,
    width: 24,
  },
  reviewImages: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    overflow: 'hidden',
  },
}));
